import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import api from '@/services/axios'

/**
 * Composable notifikasi untuk dashboard.
 *
 * @param {Object} options
 * @param {string} options.role - 'admin' | 'customer'
 * @param {string} options.endpoint - endpoint API (misal '/admin/notifications')
 * @param {number} options.pollInterval - polling interval ms (0 = matikan)
 */
export function useNotifications({
  role = 'customer',
  endpoint = '',
  pollInterval = 60000
} = {}) {
  const notifications = ref([])
  const loading = ref(false)
  const error = ref(null)

  const unreadCount = computed(
    () => notifications.value.filter((n) => !n.read).length
  )

  /**
   * Fetch dari API.
   */
  const fetchNotifications = async () => {
    if (!endpoint) return

    loading.value = true
    error.value = null

    try {
      const response = await api.get(endpoint)
      const data = response.data?.data || response.data || {}

      // Support berbagai shape response:
      // { data: [...] } | { data: { notifications: [...] } } | [...]
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.notifications)
          ? data.notifications
          : []

      notifications.value = list.map((item) =>
        normalizeNotification(item, role)
      )
    } catch (err) {
      error.value = err
      console.error(`Gagal memuat notifikasi ${role}:`, err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Normalisasi item supaya format konsisten.
   */
  const normalizeNotification = (item, role) => {
    return {
      id: item.id,
      type: item.type || 'info',
      icon: item.icon || getDefaultIcon(item.type, role),
      title: item.title || '',
      description: item.description || '',
      time: item.created_at || item.time || null,
      read: Boolean(item.read ?? item.is_read ?? false),
      to: item.to || item.link || ''
    }
  }

  /**
   * Default icon per role & type.
   */
  const getDefaultIcon = (type, role) => {
    const icons = {
      admin: {
        warning: '◷',
        danger: '!',
        info: 'ℹ',
        success: '✓'
      },
      customer: {
        warning: '⚠',
        danger: '!',
        info: 'ℹ',
        success: '✓'
      }
    }

    return icons[role]?.[type] || '🔔'
  }

  /**
   * Tandai satu notifikasi sebagai read.
   */
  const markAsRead = async (id) => {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index === -1) return

    // Optimistic update
    notifications.value[index].read = true

    try {
      await api.post(`${endpoint}/${id}/read`)
    } catch (err) {
      // Rollback kalau gagal
      notifications.value[index].read = false
      console.error('Gagal menandai notifikasi:', err)
    }
  }

  /**
   * Tandai semua sebagai read.
   */
  const markAllAsRead = async () => {
    const previous = notifications.value.map((n) => ({ ...n }))

    notifications.value.forEach((n) => {
      n.read = true
    })

    try {
      await api.post(`${endpoint}/read-all`)
    } catch (err) {
      // Rollback
      notifications.value = previous
      console.error('Gagal menandai semua:', err)
    }
  }

  /**
   * Polling interval (opsional).
   */
  let pollTimer = null

  const startPolling = () => {
    if (pollInterval <= 0) return
    stopPolling()
    pollTimer = setInterval(fetchNotifications, pollInterval)
  }

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  /**
   * ✅ Handler refresh manual dari event global.
   */
  const handleRefreshEvent = () => {
    fetchNotifications()
  }

  onMounted(() => {
    fetchNotifications()
    startPolling()

    // ✅ Listener refresh manual (dipicu dari komponen lain)
    window.addEventListener('notifications-refresh', handleRefreshEvent)
  })

  onBeforeUnmount(() => {
    stopPolling()
    window.removeEventListener('notifications-refresh', handleRefreshEvent)
  })

  return {
    notifications,
    loading,
    error,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    startPolling,
    stopPolling
  }
}